import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useLoadDataRequest } from '@futrli/shared-services';

import { ForecastTemplateGenerationPayload, generateForecastTemplate } from '../../../core/api/forecast';

interface TemplateRequest {
  workspaceId: string;
  organisationId: string;
  payload: ForecastTemplateGenerationPayload;
}

interface TemplateResult {
  // Associate the URL with its originating configuration so an obsolete result can be hidden immediately.
  request: TemplateRequest;
  data: Awaited<ReturnType<typeof generateForecastTemplate>>;
}

/**
 * Generates a template from a validated payload memoized by the caller.
 * An undefined payload disables generation and clears the previous template.
 * Disabling the hook hides the template but retains the current request/result for reuse.
 */
export const useGenerateExcelForecastTemplate = (
  workspaceId?: string,
  organisationId?: string,
  payload?: ForecastTemplateGenerationPayload,
  enabled = true
) => {
  // Identity changes only when the caller's payload or the target organisation/workspace changes.
  const request = useMemo(
    () => workspaceId && organisationId && payload ? { workspaceId, organisationId, payload } : undefined,
    [organisationId, payload, workspaceId]
  );
  const activeRequest = useRef<TemplateRequest>();
  const { data: result, error, isLoading, isLoadingOrInit, load, reset } = useLoadDataRequest<TemplateResult>();

  const reload = useCallback(() => {
    if (!enabled) {
      return;
    }

    // Explicit reloads invalidate the previous URL and any pending response.
    reset(false);
    activeRequest.current = request;
    if (request) {
      return load(
        generateForecastTemplate(request.workspaceId, request.organisationId, request.payload)
          .then(data => ({ request, data }))
      );
    }
  }, [enabled, load, request, reset]);

  useEffect(() => {
    return () => {
      activeRequest.current = undefined;
      // Invalidate only on configuration changes or unmount, not datasource switches.
      reset(false);
    };
  }, [request, reset]);

  useEffect(() => {
    if (enabled && request && activeRequest.current !== request) {
      reload();
    }
  }, [enabled, reload, request]);

  // Hide the previous request's data and status before the reset effect runs.
  const isCurrentRequest = enabled && !!request && activeRequest.current === request;
  const requestState = isCurrentRequest
    ? { error, isLoading, isLoadingOrInit }
    : { error: undefined, isLoading: false, isLoadingOrInit: false };

  return {
    hasAllParams: enabled && !!request,
    data: isCurrentRequest && result?.request === request ? result?.data : undefined,
    ...requestState,
    reload,
    reset
  };
};
